import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTranslation } from "@/i18n/useTranslation";

export interface FilterableComboboxProps<T> {
  /**
   * List of items to display in the combobox
   */
  items: T[];
  /**
   * Currently selected value
   */
  value: any;
  /**
   * Callback when a value is selected
   */
  onValueChange: (value: any) => void;
  /**
   * Function that filters items
   */
  filterFunction: (items: T[]) => T[];
  /**
   * Whether to show the filter toggle switch
   * @default true
   */
  showFilterToggle?: boolean;
  /**
   * Default state of the filter toggle
   * @default false
   */
  defaultFilterEnabled?: boolean;
  /**
   * Placeholder for the combobox button
   * @default "Select an item"
   */
  placeholder?: string;
  /**
   * Placeholder for the search input
   * @default "Search..."
   */
  searchPlaceholder?: string;
  /**
   * Message to show when no results are found
   * @default "No results found"
   */
  noResultsMessage?: string;
  /**
   * Function to get a unique value from an item
   */
  getItemValue: (item: T) => any;
  /**
   * Function to get the primary display label from an item
   */
  getItemLabel: (item: T) => string;
  /**
   * Optional function to get a secondary label (e.g., category/role) from an item
   */
  getItemSecondaryLabel?: (item: T) => string | undefined;
  /**
   * Optional custom render function for each item
   * If provided, this overrides the default rendering using primary/secondary labels
   */
  renderItem?: (item: T, isSelected: boolean) => React.ReactNode;
  /**
   * Optional label for the combobox
   */
  label?: string;
  /**
   * Whether the combobox is disabled
   */
  disabled?: boolean;
  /**
   * className for the popover trigger button
   */
  className?: string;

  modal?: boolean;
}

export function FilterableCombobox<T>({
  items,
  value,
  onValueChange,
  filterFunction,
  showFilterToggle = true,
  defaultFilterEnabled = false,
  placeholder = "Select an item",
  searchPlaceholder = "Search...",
  noResultsMessage = "No results found",
  getItemValue,
  getItemLabel,
  getItemSecondaryLabel,
  renderItem,
  label,
  disabled = false,
  className,
  modal = false,
}: FilterableComboboxProps<T>) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [filterDisabled, setFilterDisabled] = React.useState(defaultFilterEnabled);
  
  // Get the currently selected item
  const selectedItem = React.useMemo(() => {
    return items.find((item) => getItemValue(item) === value);
  }, [items, value, getItemValue]);
  
  // Apply filtering if enabled
  const displayItems = React.useMemo(() => {
    return filterDisabled ? items : filterFunction(items);
  }, [items, filterFunction, filterDisabled]);

  // Internal item renderer that handles primary and secondary labels
  const internalRenderItem = React.useCallback((item: T, isSelected: boolean) => (
    <>
      <Check
        className={cn(
          "mr-2 h-4 w-4",
          isSelected ? "opacity-100" : "opacity-0"
        )}
      />
      <div className="flex items-center justify-between w-full">
        <span>{getItemLabel(item)}</span>
        {getItemSecondaryLabel && (
          <span className="text-sm text-muted-foreground">
            {getItemSecondaryLabel(item)}
          </span>
        )}
      </div>
    </>
  ), [getItemLabel, getItemSecondaryLabel]);

  // Use provided renderItem or fallback to internal renderer
  const itemRenderer = renderItem || internalRenderItem;

  return (
    <div className="space-y-2">
      {label && (
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">
            {label}
          </label>
          {showFilterToggle && (
            <div className="flex items-center space-x-2">
              <Switch
                id="filter-toggle"
                checked={filterDisabled}
                onCheckedChange={setFilterDisabled}
              />
              <Label htmlFor="filter-toggle" className="text-sm">
                {t('common.showAll')}
              </Label>
            </div>
          )}
        </div>
      )}
      
      {!label && showFilterToggle && (
        <div className="flex items-center justify-end space-x-2">
          <Switch
            id="filter-toggle"
            checked={filterDisabled}
            onCheckedChange={setFilterDisabled}
          />
          <Label htmlFor="filter-toggle" className="text-sm">
            {t('common.showAll')}
          </Label>
        </div>
      )}
      
      <Popover open={open} onOpenChange={setOpen} modal={modal}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn("w-full justify-between", className)}
            disabled={disabled}
          >
            {selectedItem ? getItemLabel(selectedItem) : placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0">
          <Command>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              <CommandEmpty>{noResultsMessage}</CommandEmpty>
              <CommandGroup>
                {displayItems.map((item) => {
                  const itemValue = getItemValue(item);
                  const isSelected = itemValue === value;
                  
                  return (
                    <CommandItem
                      key={itemValue}
                      value={getItemLabel(item)}
                      onSelect={() => {
                        onValueChange(isSelected ? undefined : itemValue);
                        setOpen(false);
                      }}
                    >
                      {itemRenderer(item, isSelected)}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}